import { useSafeLiveQuery } from "@/indexedDB/hooks/useSafeLiveQuery";
import {
  getLatestMessagesByChatIdFromLocalDB,
  getMessageCountByChatIdFromLocalDB,
} from "@/indexedDB/services/messages";
import { useNavigationState } from "@/zustand/NavigationState";
import MessageListItem from "./MessageListItem";
import { MessageCategory } from "@/server/types/message";
import ServiceMessageListItem from "./ServiceMessageListItem";
import { memo, useRef, useEffect, useCallback } from "react";
import { MessageEntity } from "@/indexedDB/types";
import { getMessageGrouping } from "./utils/messageGrouping";
import { Button } from "@/components/primitives/button";
import { ChevronDownIcon } from "lucide-react";
import { useIntersectionObserver } from "@uidotdev/usehooks";
import { MESSAGE_LIST_PAGE_SIZE } from "@/config/constants";
import { useTranslation } from "react-i18next";
import { formatDateLabel } from "@/utils/dates";
import { areMessageItemPropsEqual } from "./utils/messageComparison";
import { useUserAvatarMapState } from "@/zustand/UserAvatarMapState";
import TypingRow from "@/components/chat/typing/TypingRow";
import { ErrorBoundary } from "@/components/error-boundary";
import { useScrollToLatestMessage } from "./hooks/useScrollToLatestMessage";

/**
 * Memoized message item component to prevent unnecessary re-renders
 * Uses custom equality check to ensure it only re-renders when props actually change
 */
const MessageItem = memo(function MessageItem({
  message,
  isFirstInGroup,
  isLastInGroup,
}: {
  message: MessageEntity;
  isFirstInGroup: boolean;
  isLastInGroup: boolean;
}) {
  if (message.category === MessageCategory.MESSAGE) {
    return (
      <MessageListItem
        message={message}
        isFirstInGroup={isFirstInGroup}
        isLastInGroup={isLastInGroup}
      />
    );
  }

  return <ServiceMessageListItem message={message} />;
}, areMessageItemPropsEqual);

/**
 * Memoized row wrapper that encapsulates the entire per-message row JSX
 * (wrapper divs, date label, and MessageItem). This prevents parent state
 * changes (e.g., showScrollToBottom toggling from intersection observers)
 * from re-rendering every message row.
 */
interface MemoizedMessageRowProps {
  message: MessageEntity;
  index: number;
  isFirstInGroup: boolean;
  isLastInGroup: boolean;
  showDateLabel: boolean;
  currentMessageDate: string;
  /** Attached to the newest row (index 0) to drive scroll-button visibility */
  observedRef?: React.RefCallback<HTMLDivElement>;
}

function areMemoizedRowPropsEqual(
  prev: MemoizedMessageRowProps,
  next: MemoizedMessageRowProps,
): boolean {
  return (
    prev.index === next.index &&
    prev.observedRef === next.observedRef &&
    prev.isFirstInGroup === next.isFirstInGroup &&
    prev.isLastInGroup === next.isLastInGroup &&
    prev.showDateLabel === next.showDateLabel &&
    prev.currentMessageDate === next.currentMessageDate &&
    areMessageItemPropsEqual(
      {
        message: prev.message,
        isFirstInGroup: prev.isFirstInGroup,
        isLastInGroup: prev.isLastInGroup,
      },
      {
        message: next.message,
        isFirstInGroup: next.isFirstInGroup,
        isLastInGroup: next.isLastInGroup,
      },
    )
  );
}

export const MemoizedMessageRow = memo(function MemoizedMessageRow({
  message,
  index,
  isFirstInGroup,
  isLastInGroup,
  showDateLabel,
  currentMessageDate,
  observedRef,
}: MemoizedMessageRowProps) {
  return (
    <div ref={observedRef} data-index={index} data-message-id={message.id}>
      <div className="w-full lg:mx-auto lg:max-w-[1000px]">
        {showDateLabel && (
          <div className="mx-auto my-2 w-fit rounded-full bg-muted px-3 py-1 text-xs text-muted-foreground">
            {currentMessageDate}
          </div>
        )}
        {/*
          Defense-in-depth backstop for csov/ow2-chat-fe#942 (iOS Safari sibling
          of #914/#923). The PRIMARY mitigation is the list-container
          `translate="no"` opt-out (below): iOS Safari's native Translate
          reparents/replaces React-managed text nodes, desyncing react-dom's
          reconciler and crashing it (`RangeError: Maximum call stack size
          exceeded` on iOS; `NotFoundError: insertBefore` on Chrome). Opting the
          whole list out of translation is what actually prevents the crash.

          SCOPE OF THIS BOUNDARY (important — it does NOT catch the #942 throw):
          React error boundaries only catch throws raised SYNCHRONOUSLY during a
          DESCENDANT component's render. They do NOT catch throws originating in
          react-dom's own reconciler/commit-phase DOM mutation (the actual #942
          class — a native Translate mutation surfacing inside react-dom
          internals), which escape to window.onerror / the route boundary
          regardless of this wrapper. So this boundary is purely a backstop for
          IN-RENDER component throws on a single row (e.g. the #914/#923-style
          markdown-on-mutated-input case) — it degrades ONE bubble to a
          placeholder while the list keeps rendering, instead of one bad row's
          render throw taking down the route. It is intentionally per-row (inside
          the `.map`, above MessageItem = MessageListItem OR
          ServiceMessageListItem). resetKeys includes the message text so an edit
          re-attempts the render (matches MessageText.tsx).
        */}
        <ErrorBoundary
          scope="panel"
          resetKeys={[message._clientId, message.content.text]}
          fallback={
            <p
              className="notranslate px-2 py-px text-sm text-muted-foreground lg:px-3"
              translate="no"
            >
              &nbsp;
            </p>
          }
        >
          <MessageItem
            message={message}
            isFirstInGroup={isFirstInGroup}
            isLastInGroup={isLastInGroup}
          />
        </ErrorBoundary>
      </div>
    </div>
  );
}, areMemoizedRowPropsEqual);

/**
 * MessageList component that displays messages with optimized rendering
 * Uses memoization to prevent unnecessary re-renders
 */
export default function MessageList() {
  const { t } = useTranslation();

  // Extract only the state values we need from navigation state
  const activeChatId = useNavigationState((state) => state.activeChatId);
  const activeChatType = useNavigationState((state) => state.activeChatType);
  const isActiveChatFullMessageListLoaded = useNavigationState(
    (state) => state.isActiveChatFullMessageListLoaded,
  );
  const isActiveChatInitialMessagesLoaded = useNavigationState(
    (state) => state.isActiveChatInitialMessagesLoaded,
  );

  const activeChatMessageTake = useNavigationState(
    (state) => state.activeChatMessageTake,
  );
  const setActiveChatMessageTake = useNavigationState(
    (state) => state.setActiveChatMessageTake,
  );

  // Live query messages from IndexedDB
  // Updates automatically when messages change
  const messages = useSafeLiveQuery(
    () =>
      getLatestMessagesByChatIdFromLocalDB(
        activeChatId || "",
        activeChatMessageTake,
      ),
    [activeChatId, activeChatMessageTake],
    [],
  );

  // Seed avatar map from IndexedDB messages so sender avatars display correctly.
  // setUserAvatarBatch already no-ops when nothing changed (returns same state ref).
  useEffect(() => {
    if (!messages.length) {
      return;
    }
    const entries = messages
      .filter((m) => m.sender && (m.senderAvatar || m.avatarId))
      .map((m) => ({
        username: m.sender,
        attachmentId: (m.senderAvatar || m.avatarId)!,
        timestamp: new Date(m.timestamp).toISOString(),
      }));
    if (entries.length > 0) {
      useUserAvatarMapState.getState().setUserAvatarBatch(entries);
    }
  }, [messages, activeChatId]);

  // We still need the message count for pagination, but isolate it from the render cycle
  const messageCount = useSafeLiveQuery(
    () => getMessageCountByChatIdFromLocalDB(activeChatId || ""),
    [activeChatId],
    0,
  );

  /// refs
  const scrollerRef = useRef<HTMLDivElement>(null);
  const messagesListRef = useRef<HTMLDivElement>(null);

  const [topAnchorRef, topAnchorEntry] = useIntersectionObserver({
    threshold: 0,
    root: scrollerRef.current,
    rootMargin: "0px",
  });

  // Derive both the newest-message signal AND the conversation key from the
  // same messages[0] object so they can never be transiently inconsistent:
  // activeChatId (Zustand, sync) and messages (async live query) update on
  // separate renders, so keying the conversation off activeChatId would let a
  // stale-data render seed the baseline and misfire a scroll on chat switch.
  // messages are always queried by the active chat id, so newestMessage.chatId
  // IS the active conversation once its data has loaded.
  const newestMessage = messages[0];
  const { latestRowRef, showScrollToBottom, scrollToLatestMessage } =
    useScrollToLatestMessage(scrollerRef, messagesListRef, {
      newestMessageId: newestMessage
        ? (newestMessage._clientId ?? newestMessage.id)
        : null,
      newestIsOutgoing: newestMessage?.isOutgoing ?? false,
      conversationKey: newestMessage?.chatId ?? null,
    });

  // TO DO => extract to middleware reusable func
  const prependMessages = useCallback(async () => {
    if (!messagesListRef.current) {
      return;
    }

    if (!activeChatId) {
      return;
    }

    if (!messages || messages?.length === 0) {
      return;
    }

    if (!isActiveChatInitialMessagesLoaded) {
      return;
    }

    if (!messageCount || messageCount === 0) {
      return;
    }

    if (!activeChatMessageTake || !messageCount) {
      return;
    }

    if (activeChatMessageTake === messageCount) {
      return;
    }

    if (activeChatMessageTake < messageCount) {
      setActiveChatMessageTake(activeChatMessageTake + MESSAGE_LIST_PAGE_SIZE);
    }
  }, [
    activeChatId,
    messages,
    isActiveChatInitialMessagesLoaded,
    messageCount,
    activeChatMessageTake,
    setActiveChatMessageTake,
  ]);

  useEffect(() => {
    if (messagesListRef.current && activeChatId) {
      messagesListRef.current.scrollTo({
        top: messagesListRef.current.scrollHeight,
      });
    }
  }, [messagesListRef, activeChatId]);

  useEffect(() => {
    if (!topAnchorEntry?.isIntersecting) {
      return;
    }

    // If the top anchor is intersecting, we can load more messages
    prependMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topAnchorEntry?.isIntersecting, messageCount]);

  return (
    <div
      ref={scrollerRef}
      style={{ WebkitOverflowScrolling: "touch" }}
      className="relative size-full flex-1 flex-col justify-end overflow-y-auto overflow-x-hidden fade-in"
    >
      {/*
        `translate="no"` + the `notranslate` class opt the entire message-list
        region out of browser translation (Google Translate, iOS Safari native
        Translate, etc.). Those translators reparent/replace the text nodes
        React rendered, which desyncs React's virtual DOM and crashes the
        reconciler (csov/ow2-chat-fe#914 Chrome `insertBefore`; #942 iOS Safari
        `RangeError`). #923 only opted out the markdown subtree; hoisting it to
        the list container also covers sibling text surfaces — sender names,
        parent-message previews, service rows, and date labels. Per-row
        ErrorBoundaries (above) are the backstop if Translate races this opt-out.
      */}
      <div
        ref={messagesListRef}
        style={{ WebkitOverflowScrolling: "touch" }}
        data-message-scroll-container
        translate="no"
        className="custom-scroll custom-scroll-x notranslate relative flex size-full flex-col-reverse overflow-y-auto overflow-x-hidden overscroll-contain"
      >
        {/* Spacer: keeps the newest message / typing row 4px off the
            container's bottom edge (first child = visual bottom in
            flex-col-reverse). */}
        <div className="h-1 w-full" />

        {/* Typing row — in flex-col-reverse the visual bottom is the start of
            the reversed flow, so this pins to the bottom of the viewport.
            Renders null when no one types. */}
        <TypingRow chatId={activeChatId} chatType={activeChatType} />

        {messages.map((message: MessageEntity, index: number) => {
          const { isFirstInGroup, isLastInGroup } = getMessageGrouping(
            messages,
            index,
          );
          const currentMessageDate = formatDateLabel(message.timestamp);
          const previousMessageDate =
            index < messages.length - 1
              ? formatDateLabel(messages[index + 1].timestamp)
              : null;
          const showDateLabel = currentMessageDate !== previousMessageDate;

          return (
            <MemoizedMessageRow
              key={message._clientId}
              message={message}
              index={index}
              isFirstInGroup={isFirstInGroup}
              isLastInGroup={isLastInGroup}
              showDateLabel={showDateLabel}
              currentMessageDate={currentMessageDate}
              observedRef={index === 0 ? latestRowRef : undefined}
            />
          );
        })}

        {/* Top anchor for loading older messages.
            Note: We use "invisible" instead of removing the text to keep a stable height.
            Removing the text shrinks this div by ~24px, and iOS WebKit doesn't properly
            adjust scroll position in flex-col-reverse containers, causing a visible content shift. */}
        <div ref={topAnchorRef} className="text-md w-full p-2 text-center">
          <span
            className={isActiveChatFullMessageListLoaded ? "invisible" : ""}
          >
            {t("loading")}
          </span>
        </div>
      </div>

      {showScrollToBottom && (
        <Button
          size="icon"
          className="absolute bottom-3 left-1/2 z-30 size-10 -translate-x-1/2 rounded-full bg-primary p-3 text-primary-foreground shadow-lg"
          onClick={() => scrollToLatestMessage()}
        >
          <ChevronDownIcon className="size-7" />
        </Button>
      )}
    </div>
  );
}
